/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getRadiationTechniques } from 'app/entities/radiation-technique/radiation-technique.reducer';
import { getEntities as getRadiationBeamParameters } from 'app/entities/radiation-beam-parameter/radiation-beam-parameter.reducer';
import { getEntities as getRadiationDosimetries } from 'app/entities/radiation-dosimetry/radiation-dosimetry.reducer';
import { createEntity, getEntity, reset, updateEntity } from './radiation-type.reducer';

export const RadiationTypeUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const radiationTechniques = useAppSelector(state => state.radiationTechnique.entities);
  const radiationBeamParameters = useAppSelector(state => state.radiationBeamParameter.entities);
  const radiationDosimetries = useAppSelector(state => state.radiationDosimetry.entities);
  const radiationTypeEntity = useAppSelector(state => state.radiationType.entity);
  const loading = useAppSelector(state => state.radiationType.loading);
  const updating = useAppSelector(state => state.radiationType.updating);
  const updateSuccess = useAppSelector(state => state.radiationType.updateSuccess);

  const handleClose = () => {
    navigate(`/radiation-type${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getClinicalDictionaries({}));
    dispatch(getRadiationTechniques({}));
    dispatch(getRadiationBeamParameters({}));
    dispatch(getRadiationDosimetries({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...radiationTypeEntity,
      ...values,
      modality: clinicalDictionaries.find(it => it.id.toString() === values.modality?.toString()),
      acquisitionParameters: clinicalDictionaries.find(it => it.id.toString() === values.acquisitionParameters?.toString()),
      energy: clinicalDictionaries.find(it => it.id.toString() === values.energy?.toString()),
      beamModality: clinicalDictionaries.find(it => it.id.toString() === values.beamModality?.toString()),
      technique: radiationTechniques.find(it => it.id.toString() === values.technique?.toString()),
      beamParams: mapIdList(values.beamParams),
      dosimetries: mapIdList(values.dosimetries),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...radiationTypeEntity,
          modality: radiationTypeEntity?.modality?.id,
          acquisitionParameters: radiationTypeEntity?.acquisitionParameters?.id,
          energy: radiationTypeEntity?.energy?.id,
          beamModality: radiationTypeEntity?.beamModality?.id,
          technique: radiationTypeEntity?.technique?.id,
          beamParams: radiationTypeEntity?.beamParams?.map(e => e.id.toString()),
          dosimetries: radiationTypeEntity?.dosimetries?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.radiationType.home.createOrEditLabel" data-cy="RadiationTypeCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.radiationType.home.createOrEditLabel">Create or edit a RadiationType</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="radiation-type-id"
                  label={translate('tinyMedicRestApp.radiationType.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.name')}
                id="radiation-type-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.description')}
                id="radiation-type-description"
                name="description"
                data-cy="description"
                type="text"
                validate={{
                  maxLength: { value: 3000, message: translate('entity.validation.maxlength', { max: 3000 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.active')}
                id="radiation-type-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.cursor')}
                id="radiation-type-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.lastUpdated')}
                id="radiation-type-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.microSec')}
                id="radiation-type-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.timestampEntry')}
                id="radiation-type-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="radiation-type-modality"
                name="modality"
                data-cy="modality"
                label={translate('tinyMedicRestApp.radiationType.modality')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-type-acquisitionParameters"
                name="acquisitionParameters"
                data-cy="acquisitionParameters"
                label={translate('tinyMedicRestApp.radiationType.acquisitionParameters')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-type-energy"
                name="energy"
                data-cy="energy"
                label={translate('tinyMedicRestApp.radiationType.energy')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-type-beamModality"
                name="beamModality"
                data-cy="beamModality"
                label={translate('tinyMedicRestApp.radiationType.beamModality')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="radiation-type-technique"
                name="technique"
                data-cy="technique"
                label={translate('tinyMedicRestApp.radiationType.technique')}
                type="select"
              >
                <option value="" key="0" />
                {radiationTechniques
                  ? radiationTechniques.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.beamParam')}
                id="radiation-type-beamParam"
                data-cy="beamParam"
                type="select"
                multiple
                name="beamParams"
              >
                <option value="" key="0" />
                {radiationBeamParameters
                  ? radiationBeamParameters.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.radiationType.dosimetry')}
                id="radiation-type-dosimetry"
                data-cy="dosimetry"
                type="select"
                multiple
                name="dosimetries"
              >
                <option value="" key="0" />
                {radiationDosimetries
                  ? radiationDosimetries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/radiation-type" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RadiationTypeUpdate;
